import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Button } from '@mui/material';
import fileDownload from 'js-file-download';
import Select from '../components/Select'
import Grid from '../components/Grid';
import columns from '../utils/OpenEntriesColumn';
import { getOpenentriesData, getCustomerEntries, getMonthList, 
    getInfo, wrapperCall, findSequence, getInitialState } from '../utils/utils';
import format from '../utils/OutputFormat';


export default function SearchForm() {
    const [info, setInfo] = useState(getInfo);
    const [customerList, setCustomerList] = useState();
    const [monthList, setMonthList] = useState();
    const [gridVisible, setGridVisible] = useState(false);
    const [activeCalls, setActiveCalls] = useState(getInitialState());
    const [noDataMsg, setNoDataMsg] = useState(false);
    const token = useSelector(state => state.auth.token);
    const rowData = useSelector(state => state.openentry.rowData);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        if(customerList === undefined) {
            getCustomerEntries(token, setCustomerList, navigate);
        }

        if(monthList === undefined) {
            getMonthList(setMonthList);
        }

        if(info['customerID']  &&  info['monthEndDate']) {
            getOpenentriesData(dispatch, info, token, setGridVisible, setNoDataMsg, navigate);
        }
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();
        console.log("Handle Sumbit");

        if(info['customerID']  &&  info['monthEndDate']) {
            getOpenentriesData(dispatch, info, token, setGridVisible, setNoDataMsg, navigate);
        } 
    };

    const handleBuySellReport = async (event) => {
        event.preventDefault();
        if(!activeCalls['buySellReport']) {
            setActiveCalls((previousValue) => ({...previousValue, 'buySellReport': true}));
            const response = await wrapperCall(
                'printBuySellReport', 
                {...info, vendor: info['customerID'], dateTo: info['monthEndDate']}, 
                token, 
                'json', 
                navigate
            );
            const boundary = response.headers['content-type'].match(/boundary=(.*)/)[1];
            const parts = response.data.split(`--${boundary}`);

            parts.forEach((part) => {
                if(part.trim()) {
                    const headersEndIndex = part.indexOf("\r\n\r\n");
                    const headers = part.slice(0, headersEndIndex).trim();
                    const body = part.slice(headersEndIndex + 4);

                    const filenameMatch = headers.match(/filename="(.+)"/);
                    const filename = filenameMatch ? filenameMatch[1] : "result.csv";

                    const blob = new Blob([body]);
                    console.log(blob);
                    fileDownload(blob, filename);
                }
            })
            setActiveCalls((previousValue) => ({...previousValue, 'buySellReport': false}));
        }
    }

    const handleStatement = async (event) => {
        event.preventDefault();
        if(!activeCalls['statement']) {
            setActiveCalls((previousValue) => ({...previousValue, 'statement': true}));
            const response = await wrapperCall(
                'printStatement', 
                {...info, endDate: info['monthEndDate']}, 
                token, 
                'blob',
                navigate
            );
            const filename = response.headers['file-name'];
            console.log(response.data);
            fileDownload(response.data, filename);
            setActiveCalls((previousValue) => ({...previousValue, 'statement': false}));
        }
    }

    const handleExportToExcel = async (event) => {
        event.preventDefault();
        if(!activeCalls['exportToExcel']) {
            setActiveCalls((previousValue) => ({...previousValue, 'exportToExcel': true}));
            const response = await wrapperCall(
                'exportToExcel', 
                info, 
                token, 
                'blob', 
                navigate
            );
            const filename = response.headers['file-name'];
            fileDownload(response.data, filename);
            setActiveCalls((previousValue) => ({...previousValue, 'exportToExcel': false}));
        }
    }

    const handleExportAllInvoices = async (event) => {
        event.preventDefault();
    
        if(!activeCalls['exportAllInvoices']) {
            setActiveCalls((previousValue) => ({...previousValue, 'exportAllInvoices': true}));
            try {
                const response = await wrapperCall(
                    'exportAllInvoices',
                    { ...info, endDate: info['monthEndDate'] },
                    token,
                    'arraybuffer',
                    navigate
                );
        
                const contentType = response.headers['content-type'];
                const boundaryMatch = contentType.match(/boundary=(.*)/);

                if (!boundaryMatch) {
                    throw new Error("Boundary not found in Content-Type header");
                }
                const boundary = `--${boundaryMatch[1]}`;
                const boundaryBytes = new TextEncoder().encode(boundary);
                const doubleCRLF = new Uint8Array([13, 10, 13, 10]); 
                const uint8Array = new Uint8Array(response.data);
                let start = 0;

                while (start < uint8Array.length) {
                    const end = findSequence(uint8Array, boundaryBytes, start);
                    if (end === -1) break;
                    const part = uint8Array.slice(start, end);
                    const headersEndIndex = findSequence(part, doubleCRLF, 0);

                    if (headersEndIndex === -1) {
                        start = end + boundaryBytes.length;
                        continue;
                    }
        
                    const headersPart = part.slice(0, headersEndIndex);
                    const body = part.slice(headersEndIndex + doubleCRLF.length);
                    const headers = new TextDecoder().decode(headersPart);
        
                    const filenameMatch = headers.match(/filename="(.+)"/);
                    const filename = filenameMatch ? filenameMatch[1] : "file.bin";
                    const contentTypeMatch = headers.match(/Content-Type: (.+)/);
                    const contentType = contentTypeMatch ? contentTypeMatch[1] : "application/octet-stream";
        
                    const blob = new Blob([body], { type: contentType });
                    fileDownload(blob, filename);
                    start = end + boundaryBytes.length;
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
        
                console.log("Files downloaded successfully!");
            } catch (error) {
                console.error("Error downloading files:", error);
            } finally {
                setActiveCalls((previousValue) => ({...previousValue, 'exportAllInvoices': false}));
            }
        }
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setInfo((previousValue) => ({...previousValue, [name]: value}));
        localStorage.setItem([name], value);
    }

    return(
        <>
            <Box
            component="form"
            className="search"
            sx={{
                display: 'flex',
                justifyContent: 'space-around',
                paddingTop: '50px'
            }}
            noValidate
            autoComplete="off"
            >
                <Box sx={{ display: 'flex', width: '60%' }}>
                    <Box sx={{ width: '60%' }}>
                        <Select
                            label="CustomerID"
                            name="customerID"
                            default="Select Customer"
                            info={info}
                            array={customerList}
                            onChange={handleChange}
                            fullWidth
                        />
                    </Box>
                    <Box sx={{ width: '25%' }}>
                        <Select
                            label="MonthEndDate"
                            name="monthEndDate"
                            default="Select Month"
                            info={info}
                            array={monthList}
                            onChange={handleChange}
                            fullWidth
                        />
                    </Box>
                    <Box sx={{ width: '15%' }}>
                        <Select
                            label="Format"
                            name="outputFormat"
                            default="Format"
                            info={info}
                            array={format}
                            onChange={handleChange}
                            fullWidth
                        />
                    </Box>
                </Box>

                <Box sx={{ alignContent: 'center'}}>
                    <Button
                        className="buttons"
                        type="submit"
                        variant="outlined"
                        color="inherit"
                        sx={{ color: 'white', backgroundColor: 'red', '&:hover': { backgroundColor: '#8b0000' }}}
                        onClick={handleSubmit}
                    >
                        Get Invoices
                    </Button>
                    <Button
                        className="buttons"
                        type="submit"
                        variant="outlined"
                        color="inherit"
                        sx={{ color: 'white', backgroundColor: 'red', '&:hover': { backgroundColor: '#8b0000' }}}
                        onClick={handleStatement}
                    >
                        Statement
                    </Button>
                    <Button
                        className="buttons"
                        type="submit"
                        variant="outlined"
                        color="inherit"
                        sx={{ color: 'white', backgroundColor: 'red', '&:hover': { backgroundColor: '#8b0000' }}}
                        onClick={handleBuySellReport}
                    >
                        Buy/Sell Report
                    </Button>
                </Box>
            </Box>


            { gridVisible && <Box sx={{ paddingBottom: "50px"}}>
                <Box className="table">
                    <Grid rowData={rowData} columnDefs={columns} />
                </Box>
                <Box sx={{ display: 'flex', gap: '20px', position:"relative", left:"60%", width:"40%" }}>
                    <Button
                        className="buttons"
                        type="submit"
                        variant="outlined"
                        color="inherit"
                        sx={{ color: 'white', backgroundColor: 'red', '&:hover': { backgroundColor: '#8b0000' }}}
                        onClick={handleExportToExcel}
                    >
                        Export to Excel
                    </Button>
                    <Button
                        className="buttons"
                        type="submit"
                        variant="outlined"
                        color="inherit"
                        sx={{ color: 'white', backgroundColor: 'red', '&:hover': { backgroundColor: '#8b0000' }}}
                        onClick={handleExportAllInvoices}
                    >
                        Export All Invoices
                    </Button>
                </Box>
            </Box> }
            { noDataMsg && <p style={{ color: "red", textAlign: "center" }}>No data available.</p> }
        </>
    )
}